/**
 * D420: the motorsport theme's art, once for both renderers, built from the compute theme's
 * pieces (`Unit`s of boxes, cylinders, rods, panels and discs, `iso-kit-compute-art.ts`): the
 * flat map paints them in SVG (`iso-kit-motorsport.ts`), the 3D map builds them as instanced
 * meshes (`idle/three/motorsport.ts`), so the two agree on what stands where (D366).
 *
 * A team grows from a lock-up garage to a works team. The go-karts' first tile is the lock-up,
 * and it gains storeys with their milestones; every other producer's plot reads as what it is
 * (karts on a little track, club cars in their bays, tyre racks, pit garages, rally cars on
 * gravel, the wind tunnel's duct loop, the dyno cells' stacks, a hypercar atelier) and grows
 * tall as well as wide with its tier (D350). Every name, livery and shape here is original:
 * no real team, car, circuit, sponsor or maker (D9, D420).
 */
import type { IsoBuildingAt } from './IsoWorldMap.tsx';
import type { Bounds, IsoParcel } from './iso.ts';
import {
  C,
  FLOOR,
  block,
  doors,
  generator,
  hashXY,
  inset,
  mainRect,
  parapet,
  plinth,
  roofDetail,
  roofPlant,
  steps,
  tilesOf,
  type Facade,
  type Look,
  type Piece,
  type Unit,
} from './iso-kit-compute-art.ts';

/* ---------- the theme's colours (original, D9) ---------- */

export const M = {
  /** The team's own colours: a deep teal with an orange flash. */
  team: '#1E8C8A',
  flash: '#F08A24',
  tarmac: '#44474D',
  paddock: '#585B61',
  concrete: '#C9C6BE',
  pad: '#B9B6AE',
  gravel: '#CDBB8E',
  mud: '#8A6F4E',
  kerbRed: '#D23A2E',
  kerbWhite: '#F1EFE9',
  line: '#F2EFE6',
  tyre: '#26282C',
  tyreSide: '#3A3D42',
  /** Tyre compounds, as a band round a stack. */
  soft: '#E0453A',
  medium: '#F2C744',
  hard: '#EDEDED',
  wet: '#3D86D6',
  glass: '#33404D',
  glassLight: '#7FA6C4',
  white: '#ECEEF0',
  steel: '#8A9096',
  dark: '#2B2E33',
  warm: '#FFD27A',
  gold: '#D9B24A',
  canvas: '#F2F0EA',
  grass: '#5FA35A',
} as const;

/** The liveries cars are painted in, picked by place. */
export const LIVERIES = [
  '#1E8C8A',
  '#E8EAEC',
  '#C9352B',
  '#2F5FA8',
  '#F0B429',
  '#2E3238',
  '#7A3E9D',
  '#3E8E4A',
  '#F08A24',
] as const;

/** D291: the building paint decorations, and the roofs. */
export const PAINT: Record<string, string> = {
  paint_white: '#EEF0F2',
  paint_red: '#C4382E',
  paint_blue: '#2F5FA8',
  paint_black: '#2E3238',
  paint_stripes: '#E6E1D6',
  paint_checks: '#D9DCDF',
  paint_silver: '#B9BFC6',
};
export const ROOFS: Record<string, string> = {
  roof_grey: '#7C838B',
  roof_red: '#A8473A',
  roof_glass: '#9DC0D8',
  roof_solar: '#2C4A78',
};
/** D331: the tyre walls (the boundary), by decoration: the tyres and the band round them. */
export const TYRE_WALLS: Record<string, { tyre: string; band: string; rows: number }> = {
  tyres_black: { tyre: M.tyre, band: M.tyreSide, rows: 2 },
  tyres_white: { tyre: M.tyre, band: '#ECEEF0', rows: 2 },
  tyres_red: { tyre: M.tyre, band: '#D23A2E', rows: 2 },
  tyres_blue: { tyre: M.tyre, band: '#2F6BC0', rows: 2 },
  tyres_tall: { tyre: M.tyre, band: M.tyreSide, rows: 3 },
  tyres_decals: { tyre: M.tyre, band: '#F0B429', rows: 2 },
};
/** D331: the gantry over the gate. */
export const GANTRIES: Record<string, { c: string; lights?: boolean; flags?: boolean }> = {
  gantry_plain: { c: '#9AA1AA' },
  gantry_flags: { c: '#9AA1AA', flags: true },
  gantry_lights: { c: '#3B4048', lights: true },
  gantry_arch: { c: '#ECEEF0' },
  gantry_lap_board: { c: '#2E3238', lights: true },
};
/** D331: the kerb decoration paints the paddock's lines and the service road's kerbs. */
export const KERBS: Record<string, string> = {
  kerb_red: '#D23A2E',
  kerb_blue: '#2F6BC0',
  kerb_green: '#3E9A4E',
  kerb_yellow: '#F0C33C',
  kerb_checks: '#2E3238',
  kerb_gold: '#D9B24A',
};
/** D331: the team's transporter, by the vehicle decoration. */
export const TRUCKS: Record<string, string> = {
  truck_red: '#C4382E',
  truck_green: '#3E7A46',
  truck_blue: '#2E5F9E',
  truck_yellow: '#E0B83A',
  truck_orange: '#D9752B',
  truck_vintage: '#8FB3A2',
  truck_show: '#7A3E9D',
};
/** D331: the mechanics' overalls. */
export const OVERALLS: Record<string, string> = {
  overalls_plain: '#4A5260',
  overalls_team: M.team,
  overalls_hi_vis: '#E6E23A',
  overalls_fire: '#D9473A',
  overalls_rain: '#2F6BC0',
  overalls_blazer: '#2C3038',
  overalls_gold: '#D9B24A',
};
/** D380: who calls at the paddock gate, by the data's visitor id. */
export const PADDOCK_VISITOR: Record<
  string,
  { coat: string; trim: string; ride?: { body: string; long: number; tall: number } }
> = {
  scrutineer: { coat: '#F2F0EA', trim: '#2E3238' },
  courier: { coat: '#D9752B', trim: '#F2EFE6', ride: { body: '#E9ECEF', long: 0.6, tall: 0.3 } },
  fan: { coat: '#C4382E', trim: '#F0B429' },
  race_director: {
    coat: '#2C3038',
    trim: '#D9B24A',
    ride: { body: '#ECEEF0', long: 0.5, tall: 0.2 },
  },
  photographer: { coat: '#3E7A46', trim: '#E9ECEF' },
};
/** D380: a cash prize found in the paddock: a presentation cheque on its stand. */
export const PRIZE = {
  board: '#F7F5EF',
  edge: '#D9B24A',
  legs: '#6B5A44',
  ink: '#2F5FA8',
} as const;

/* ---------- small helpers ---------- */

const pick = <T>(list: readonly T[], x: number, y: number, salt: number): T =>
  list[Math.floor(hashXY(x, y, salt) * list.length) % list.length] as T;

const livery = (x: number, y: number, salt = 3): string => pick(LIVERIES, x, y, salt);

/** A box piece. */
function bx(
  x: number,
  y: number,
  w: number,
  d: number,
  z: number,
  h: number,
  c: string,
  more: Partial<Extract<Piece, { s: 'box' }>> = {},
): Piece {
  return { s: 'box', x, y, w, d, z, h, c, ...more };
}

/* ---------- cars ---------- */

export type CarKind = 'kart' | 'saloon' | 'rally' | 'touring' | 'gt' | 'proto' | 'open' | 'hyper';

/**
 * A car of `kind`, its middle at (cx, cy), lying along x (`alongX`) or along y, nose to +.
 * Built in its own frame (u along it, v across it) and turned onto the ground.
 */
export function car(
  cx: number,
  cy: number,
  alongX: boolean,
  kind: CarKind,
  paint: string,
  z = 0,
): Piece[] {
  const out: Piece[] = [];
  const b = (
    u0: number,
    v0: number,
    lu: number,
    lv: number,
    z0: number,
    h: number,
    c: string,
    more: Partial<Extract<Piece, { s: 'box' }>> = {},
  ) =>
    out.push(
      alongX
        ? bx(cx + u0, cy + v0, lu, lv, z + z0, h, c, more)
        : bx(cx + v0, cy + u0, lv, lu, z + z0, h, c, more),
    );
  const wheels = (u: number, v: number, l: number, w: number, h: number) => {
    for (const su of [-1, 1])
      for (const sv of [-1, 1]) b(su * u - l / 2, sv * v - w / 2, l, w, 0, h, M.tyre);
  };
  const glass = M.glass;
  switch (kind) {
    case 'kart':
      b(-0.13, -0.07, 0.26, 0.14, 0.015, 0.02, M.dark);
      b(-0.09, -0.08, 0.17, 0.16, 0.025, 0.025, paint, { roof: true });
      b(-0.05, -0.035, 0.07, 0.07, 0.05, 0.055, M.dark);
      out.push({ s: 'ball', x: cx, y: cy, r: 0.032, z: z + 0.1, c: paint });
      wheels(0.1, 0.075, 0.05, 0.03, 0.045);
      break;
    case 'saloon':
    case 'rally':
    case 'touring': {
      const low = kind === 'touring' ? 0.03 : kind === 'rally' ? 0.05 : 0.04;
      b(-0.25, -0.12, 0.5, 0.24, low, 0.09, paint, { roof: true });
      b(-0.11, -0.1, 0.24, 0.2, low + 0.09, 0.07, glass, { top: paint, roof: true });
      wheels(0.16, 0.1, 0.1, 0.05, low + 0.04);
      if (kind === 'rally') {
        b(-0.255, -0.125, 0.51, 0.25, low, 0.03, M.mud);
        b(0.08, -0.08, 0.04, 0.16, low + 0.16, 0.025, M.white);
      }
      if (kind === 'touring') {
        b(-0.25, -0.004, 0.5, 0.008, low + 0.091, 0.002, M.white);
        b(-0.26, -0.11, 0.05, 0.22, low + 0.18, 0.015, M.dark);
        b(-0.25, -0.09, 0.02, 0.02, low + 0.09, 0.09, M.dark);
        b(-0.25, 0.07, 0.02, 0.02, low + 0.09, 0.09, M.dark);
      }
      break;
    }
    case 'gt':
      b(-0.28, -0.125, 0.56, 0.25, 0.035, 0.08, paint, { roof: true });
      b(-0.14, -0.1, 0.2, 0.2, 0.115, 0.06, glass, { top: paint, roof: true });
      b(0.06, -0.004, 0.22, 0.008, 0.116, 0.002, M.white);
      wheels(0.18, 0.11, 0.1, 0.05, 0.08);
      break;
    case 'proto':
      b(-0.29, -0.135, 0.58, 0.27, 0.025, 0.07, paint, { roof: true });
      b(-0.04, -0.06, 0.18, 0.12, 0.095, 0.055, glass, { top: shadeHex(paint), roof: true });
      b(-0.26, -0.008, 0.22, 0.016, 0.095, 0.08, paint);
      b(-0.3, -0.135, 0.06, 0.27, 0.15, 0.015, M.dark);
      b(0.28, -0.12, 0.02, 0.24, 0.04, 0.02, M.warm, { glow: true });
      wheels(0.19, 0.12, 0.1, 0.04, 0.075);
      break;
    case 'open':
      b(-0.25, -0.05, 0.5, 0.1, 0.03, 0.05, paint, { roof: true });
      b(-0.1, -0.1, 0.18, 0.2, 0.03, 0.045, paint, { roof: true });
      b(-0.02, -0.03, 0.08, 0.06, 0.08, 0.03, M.dark);
      b(0.22, -0.13, 0.05, 0.26, 0.02, 0.015, M.dark);
      b(-0.27, -0.1, 0.05, 0.2, 0.12, 0.04, M.dark);
      wheels(0.17, 0.15, 0.1, 0.06, 0.09);
      break;
    case 'hyper':
      b(-0.28, -0.13, 0.56, 0.26, 0.03, 0.065, paint, { roof: true });
      b(-0.08, -0.08, 0.2, 0.16, 0.095, 0.05, glass, { top: paint, roof: true });
      b(-0.28, -0.13, 0.05, 0.26, 0.16, 0.02, M.dark);
      b(-0.26, -0.09, 0.02, 0.02, 0.095, 0.07, M.dark);
      b(-0.26, 0.07, 0.02, 0.02, 0.095, 0.07, M.dark);
      wheels(0.18, 0.12, 0.1, 0.05, 0.08);
      break;
  }
  return out;
}

/** A colour a little darker, for a canopy's frame against its body. */
function shadeHex(c: string): string {
  const n = parseInt(c.slice(1), 16);
  const f = (v: number) => Math.max(0, Math.round(v * 0.7));
  const r = f((n >> 16) & 255),
    g = f((n >> 8) & 255),
    b = f(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/** A stack of `n` tyres at (x, y), its compound's band round the top one. */
function tyres(x: number, y: number, n: number, band: string, z = 0, r = 0.075): Piece[] {
  const out: Piece[] = [];
  for (let i = 0; i < n; i++)
    out.push({ s: 'cyl', x, y, r, z: z + i * 0.06, h: 0.055, c: M.tyre, top: M.tyreSide });
  out.push({ s: 'cyl', x, y, r: r * 1.01, z: z + (n - 1) * 0.06 + 0.02, h: 0.015, c: band });
  return out;
}

const COMPOUNDS = [M.soft, M.medium, M.hard, M.wet] as const;

/** Red and white kerb blocks along a line on the ground, from (x0, y0) for `len` along x or y. */
function kerb(x0: number, y0: number, len: number, alongX: boolean, z = 0): Piece[] {
  const out: Piece[] = [];
  const n = Math.max(2, Math.round(len / 0.12));
  const step = len / n;
  for (let i = 0; i < n; i++) {
    const c = i % 2 ? M.kerbWhite : M.kerbRed;
    out.push(
      alongX
        ? bx(x0 + i * step, y0, step, 0.07, z, 0.012, c)
        : bx(x0, y0 + i * step, 0.07, step, z, 0.012, c),
    );
  }
  return out;
}

/** A canopy on four posts over a footprint: a team awning, a service gazebo, a carport. */
function canopy(x: number, y: number, w: number, d: number, h: number, c: string): Piece[] {
  const out: Piece[] = [];
  for (const [px, py] of [
    [x, y],
    [x + w, y],
    [x, y + d],
    [x + w, y + d],
  ] as const)
    out.push({ s: 'rod', a: [px, py, 0], b: [px, py, h], t: 0.02, c: M.steel });
  out.push(bx(x - 0.02, y - 0.02, w + 0.04, d + 0.04, h, 0.03, c, { roof: true }));
  out.push(bx(x - 0.02, y + d + 0.015, w + 0.04, 0.01, h - 0.05, 0.05, c));
  return out;
}

/** A garage bay's front: an open door with the lit bay inside, a fascia in the team's colours. */
function openBay(x: number, y: number, w: number, h: number, fascia: string): Piece[] {
  return [
    bx(x, y - 0.012, w, 0.014, 0, h, M.dark, {
      marks: { kind: 'band', rows: 1, cols: 1, c: M.dark, on: '#F6E7C4', z0: 0.62, z1: 0.92 },
    }),
    bx(x - 0.03, y - 0.02, w + 0.06, 0.022, h + 0.01, 0.06, fascia),
  ];
}

/** A plain hall over a footprint: walls, a flat roof with its plant, doors on its front. */
function hallOn(
  r: { x: number; y: number; w: number; d: number },
  floors: number,
  wall: string,
  roof: string,
  face: Facade,
  plant: number,
): { pieces: Piece[]; fine: Piece[]; h: number } {
  const h = floors * FLOOR + 0.08;
  const pieces: Piece[] = [
    plinth(r.x, r.y, r.w, r.d),
    bx(r.x, r.y, r.w, r.d, 0, h, wall, {
      top: roof,
      roof: true,
      face,
      marks: {
        kind: 'win',
        rows: floors,
        cols: Math.max(1, Math.round(Math.max(r.w, r.d) * 3)),
        c: C.window,
        on: C.lit,
        z0: 0.06,
        z1: 0.96,
      },
    }),
    // The team's band along the top of the walls.
    bx(r.x - 0.006, r.y - 0.006, r.w + 0.012, r.d + 0.012, h - 0.07, 0.035, M.team),
    ...doors(r.x, r.y, r.w, r.d),
  ];
  if (plant > 0) pieces.push(...roofPlant(r.x + 0.1, r.y + 0.1, r.w - 0.2, r.d - 0.3, h, plant));
  const fine = [...parapet(r.x, r.y, r.w, r.d, h, wall), ...roofDetail(r.x, r.y, r.w, r.d, h)];
  return { pieces, fine, h };
}

/* ---------- the producers ---------- */

type Model = (p: IsoParcel, L: Look) => Unit[];

const tile = (p: IsoParcel, x: number, y: number, pieces: Piece[], fine?: Piece[]): Unit => ({
  x,
  y,
  w: 1,
  d: 1,
  pieces,
  ...(fine ? { fine } : {}),
  id: p.id as string,
});

/** The plot's tiles outside its biggest rectangle, for its smaller kit. */
function rest(p: IsoParcel, r: Bounds): Array<[number, number]> {
  return tilesOf(p).filter(([x, y]) => !(x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h));
}

/**
 * Go-karts: where the team began. The first tile is the lock-up garage, its roller door in the
 * team's colours, a workshop rising over it with the milestones; every other tile is a stretch
 * of kart track with its kerbs, karts on it, more of them as the plot grows.
 */
const goKarts: Model = (p) => {
  const tier = p.tier ?? 0;
  const out: Unit[] = [];
  const T = tilesOf(p);
  const first = T[0];
  if (!first) return out;
  const [gx, gy] = first;
  const floors = 1 + steps(tier, 3, 4);
  const g: Piece[] = [
    plinth(gx + 0.1, gy + 0.1, 0.8, 0.8),
    bx(gx + 0.1, gy + 0.1, 0.8, 0.8, 0, 0.4, '#D8D2C4', { top: C.roof, roof: true, face: 'clad' }),
    bx(gx + 0.22, gy + 0.88, 0.56, 0.03, 0, 0.3, M.team, {
      face: 'shutter',
      marks: { kind: 'grille', rows: 5, cols: 1, c: shadeHex(M.team) },
    }),
    bx(gx + 0.22, gy + 0.9, 0.56, 0.02, 0.31, 0.04, M.flash),
  ];
  if (floors > 1)
    g.push(
      ...block(gx + 0.12, gy + 0.12, 0.76, 0.76, floors - 1, M.white, C.roof, C.window, C.lit, 0.4),
    );
  out.push({ x: gx, y: gy, w: 1, d: 1, pieces: g, id: p.id as string });
  const karts = 1 + steps(tier, 4, 2);
  for (const [x, y] of T.slice(1)) {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.012, M.tarmac)];
    pieces.push(...kerb(x + 0.04, y + 0.04, 0.92, true, 0.012));
    pieces.push(...kerb(x + 0.04, y + 0.89, 0.92, true, 0.012));
    for (let i = 0; i < karts; i++) {
      const u = 0.22 + ((i + hashXY(x, y, 11)) % 3) * 0.26;
      pieces.push(...car(x + u, y + 0.35 + (i % 2) * 0.3, true, 'kart', livery(x + i, y), 0.012));
    }
    pieces.push(...tyres(x + 0.88, y + 0.5, 3, M.tyreSide, 0.012, 0.06));
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/** Club cars: a club saloon in each bay; a carport over the bays as the club grows. */
const clubCars: Model = (p) => {
  const tier = p.tier ?? 0;
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [bx(x + 0.06, y + 0.06, 0.88, 0.88, 0, 0.01, M.paddock)];
    pieces.push(bx(x + 0.1, y + 0.5, 0.8, 0.012, 0.01, 0.002, M.line));
    pieces.push(...car(x + 0.5, y + 0.28, true, 'saloon', livery(x, y), 0.01));
    pieces.push(...car(x + 0.5, y + 0.72, true, 'saloon', livery(x, y, 5), 0.01));
    if (tier >= 2) pieces.push(...canopy(x + 0.12, y + 0.1, 0.76, 0.8, 0.4, '#D5D9DD'));
    return tile(p, x, y, pieces);
  });
};

/**
 * Tyre bays: the tyre store's hall, and racks of tyres stacked by compound on the rest, a level
 * more as the plot grows.
 */
const tyreBays: Model = (p) => {
  const tier = p.tier ?? 0;
  const out: Unit[] = [];
  const r = mainRect(p.tiles);
  const levels = 1 + steps(tier, 3, 3);
  if (r.w >= 2 && r.h >= 1) {
    const i = inset(r.x, r.y, Math.min(r.w, 3), 1, 0.08);
    const h = hallOn(i, 1 + steps(tier, 4, 2), '#DADDE0', C.roof, 'ribbed', 2);
    // A giant tyre on the roof: the store's own sign.
    h.pieces.push(
      { s: 'cyl', x: i.x + i.w * 0.75, y: i.y + 0.4, r: 0.22, z: h.h, h: 0.12, c: M.tyre },
      { s: 'cyl', x: i.x + i.w * 0.75, y: i.y + 0.4, r: 0.11, z: h.h + 0.121, h: 0.005, c: M.soft },
    );
    out.push({
      x: r.x,
      y: r.y,
      w: Math.min(r.w, 3),
      d: 1,
      pieces: h.pieces,
      fine: h.fine,
      id: p.id as string,
    });
  }
  const used = new Set(
    r.w >= 2 ? Array.from({ length: Math.min(r.w, 3) }, (_, k) => `${r.x + k},${r.y}`) : [],
  );
  for (const [x, y] of tilesOf(p)) {
    if (used.has(`${x},${y}`)) continue;
    const pieces: Piece[] = [bx(x + 0.06, y + 0.06, 0.88, 0.88, 0, 0.01, M.concrete)];
    for (let l = 0; l < levels; l++) {
      const z = 0.01 + l * 0.26;
      // A shelf over each level but the top, so the top level's tyres show from above.
      if (l < levels - 1) pieces.push(bx(x + 0.1, y + 0.2, 0.8, 0.6, z + 0.23, 0.02, M.steel));
      for (let a = 0; a < 3; a++)
        for (let c = 0; c < 2; c++)
          pieces.push(
            ...tyres(
              x + 0.24 + a * 0.26,
              y + 0.35 + c * 0.3,
              3,
              COMPOUNDS[(a + c + l) % 4] as string,
              z,
              0.08,
            ),
          );
    }
    for (const [px, py] of [
      [0.1, 0.2],
      [0.88, 0.2],
      [0.1, 0.78],
      [0.88, 0.78],
    ] as const)
      pieces.push(bx(x + px, y + py, 0.025, 0.025, 0, levels * 0.26 - 0.02, M.steel));
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/**
 * Pit crews: a row of pit garages, each bay open and lit, its car out on the apron; a storey of
 * team offices over the garages as the crew grows.
 */
const pitCrews: Model = (p) => {
  const tier = p.tier ?? 0;
  const floors = steps(tier, 3, 3);
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [bx(x + 0.02, y + 0.02, 0.96, 0.96, 0, 0.012, M.paddock)];
    const h = 0.4;
    pieces.push(plinth(x + 0.04, y + 0.06, 0.92, 0.46));
    pieces.push(
      bx(x + 0.04, y + 0.06, 0.92, 0.46, 0, h, M.white, {
        top: C.roof,
        roof: floors === 0,
        face: 'clad',
      }),
    );
    pieces.push(...openBay(x + 0.14, y + 0.52, 0.72, 0.3, M.team));
    if (floors > 0)
      pieces.push(
        ...block(
          x + 0.04,
          y + 0.06,
          0.92,
          0.42,
          floors,
          M.glassLight,
          C.roof,
          '#5C83A3',
          C.lit,
          h,
          'curtain',
        ),
      );
    // The pit lane's white line, and the car waiting on the apron with its tyres beside it.
    pieces.push(bx(x + 0.02, y + 0.9, 0.96, 0.02, 0.012, 0.002, M.line));
    pieces.push(...car(x + 0.5, y + 0.72, true, 'open', M.team, 0.012));
    pieces.push(...tyres(x + 0.12, y + 0.66, 2, M.soft, 0.012, 0.055));
    pieces.push(...tyres(x + 0.88, y + 0.66, 2, M.soft, 0.012, 0.055));
    return tile(p, x, y, pieces);
  });
};

/** Rally cars: a rally car on a gravel pad; a service gazebo over it as the team grows. */
const rallyCars: Model = (p) => {
  const tier = p.tier ?? 0;
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.014, M.gravel)];
    const along = hashXY(x, y, 13) < 0.5;
    pieces.push(...car(x + 0.5, y + 0.5, along, 'rally', livery(x, y, 7), 0.014));
    if (tier >= 2 || hashXY(x, y, 17) < 0.3)
      pieces.push(
        ...canopy(x + 0.14, y + 0.14, 0.72, 0.72, 0.42, hashXY(x, y, 19) < 0.5 ? M.team : M.flash),
      );
    pieces.push(...tyres(x + 0.1, y + 0.88, 2, M.mud, 0.014, 0.06));
    return tile(p, x, y, pieces);
  });
};

/**
 * Engine shops: the shop's hall with its exhaust stacks; on the rest, engines on their stands
 * under a hoist.
 */
const engineShops: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.08);
    const h = hallOn(i, 1 + steps(tier, 3, 4), '#C9CDD2', '#8E949C', 'louvre', 2);
    for (let k = 0; k < Math.min(4, 1 + Math.floor(i.w)); k++)
      h.pieces.push({
        s: 'cyl',
        x: i.x + 0.25 + k * 0.45,
        y: i.y + 0.2,
        r: 0.045,
        z: h.h,
        h: 0.35,
        c: C.flue,
        top: C.black,
      });
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces: h.pieces,
      fine: h.fine,
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r)) {
    const pieces: Piece[] = [bx(x + 0.06, y + 0.06, 0.88, 0.88, 0, 0.01, M.concrete)];
    for (const dx of [0.28, 0.68]) {
      pieces.push(bx(x + dx - 0.12, y + 0.4, 0.24, 0.2, 0, 0.1, M.steel));
      pieces.push(bx(x + dx - 0.1, y + 0.42, 0.2, 0.16, 0.1, 0.12, '#5A5F66', { roof: true }));
      pieces.push(bx(x + dx - 0.08, y + 0.44, 0.16, 0.12, 0.22, 0.04, M.flash));
    }
    pieces.push({
      s: 'rod',
      a: [x + 0.12, y + 0.5, 0],
      b: [x + 0.12, y + 0.5, 0.55],
      t: 0.03,
      c: M.medium,
    });
    pieces.push({
      s: 'rod',
      a: [x + 0.12, y + 0.5, 0.55],
      b: [x + 0.7, y + 0.5, 0.55],
      t: 0.03,
      c: M.medium,
    });
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/** Workshops: the race workshop's hall, its roller doors along the front, its band in team colours. */
const workshops: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.08);
    const h = hallOn(i, 1 + steps(tier, 2, 5), M.white, C.roof, 'ribbed', 3);
    const n = Math.max(1, Math.floor(i.w / 0.6));
    for (let k = 0; k < n; k++)
      h.pieces.push(
        bx(i.x + 0.12 + k * 0.6, i.y + i.d - 0.005, 0.4, 0.025, 0, 0.3, '#B9BCC0', {
          face: 'shutter',
          marks: { kind: 'grille', rows: 5, cols: 1, c: '#9DA1A6', front: true },
        }),
      );
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces: h.pieces,
      fine: h.fine,
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r)) {
    const pieces: Piece[] = [bx(x + 0.06, y + 0.06, 0.88, 0.88, 0, 0.01, M.concrete)];
    pieces.push(...car(x + 0.5, y + 0.5, true, 'touring', '#9AA1AA', 0.01));
    pieces.push(bx(x + 0.12, y + 0.12, 0.3, 0.14, 0.01, 0.16, M.flash, { roof: true }));
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/** Touring cars: a touring car under the team's awning, tyres stacked beside it. */
const touringCars: Model = (p) => {
  const tier = p.tier ?? 0;
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.012, M.paddock)];
    pieces.push(...car(x + 0.5, y + 0.55, true, 'touring', livery(x, y, 9), 0.012));
    pieces.push(...canopy(x + 0.12, y + 0.18, 0.76, 0.66, 0.45 + steps(tier, 5, 2) * 0.06, M.team));
    pieces.push(...tyres(x + 0.12, y + 0.12, 3, M.medium, 0.012, 0.06));
    pieces.push(...tyres(x + 0.28, y + 0.12, 3, M.medium, 0.012, 0.06));
    return tile(p, x, y, pieces);
  });
};

/**
 * Wind tunnels: the test building, and over it the tunnel's closed loop of duct on its legs, a
 * fan turning in its corner; chillers on the rest. The loop grows with the building.
 */
const windTunnels: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.08);
    const floors = 1 + steps(tier, 3, 3);
    const h = hallOn(i, floors, '#E4E7EA', '#A9AFB6', 'clad', 0);
    // The duct: a loop round the roof, square in section, its corners turned.
    const z = h.h + 0.1;
    const t = Math.min(0.32, Math.min(i.w, i.d) * 0.28);
    const duct = '#C3C8CE';
    const lx = i.x + 0.05,
      ly = i.y + 0.05,
      lw = i.w - 0.1,
      ld = i.d - 0.1;
    h.pieces.push(
      bx(lx, ly, lw, t, z, t, duct, { roof: true }),
      bx(lx, ly + ld - t, lw, t, z, t, duct, { roof: true }),
      bx(lx, ly + t, t, ld - 2 * t, z, t, duct, { roof: true }),
      bx(lx + lw - t, ly + t, t, ld - 2 * t, z, t, duct, { roof: true }),
    );
    // The test section, glazed, on the front run; the fan housing on the back corner.
    h.pieces.push(
      bx(lx + lw * 0.35, ly + ld - t - 0.01, lw * 0.3, t + 0.02, z - 0.01, t + 0.02, M.glassLight, {
        roof: true,
      }),
      bx(lx + lw - t - 0.08, ly - 0.04, t + 0.12, t + 0.08, z - 0.04, t + 0.08, '#9AA1AA', {
        roof: true,
      }),
      {
        s: 'disc',
        x: lx + lw - t / 2 + 0.02,
        y: ly + t / 2,
        r: t * 0.42,
        z: z + t + 0.042,
        c: C.dark,
        fan: true,
      },
    );
    for (const [px, py] of [
      [lx + 0.1, ly + 0.1],
      [lx + lw - 0.1, ly + ld - 0.1],
      [lx + 0.1, ly + ld - 0.1],
    ] as const)
      h.pieces.push(bx(px - 0.03, py - 0.03, 0.06, 0.06, h.h, 0.1, M.steel));
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces: h.pieces,
      fine: h.fine,
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r))
    out.push(
      tile(p, x, y, [
        bx(x + 0.07, y + 0.13, 0.86, 0.74, 0, 0.035, C.pad),
        bx(x + 0.12, y + 0.18, 0.76, 0.64, 0, 0.24, C.light, {
          top: C.mid,
          roof: true,
          face: 'louvre',
          marks: { kind: 'grille', rows: 2, cols: 3, c: C.mid },
        }),
        { s: 'disc', x: x + 0.32, y: y + 0.5, r: 0.14, z: 0.245, c: C.dark, fan: true },
        { s: 'disc', x: x + 0.68, y: y + 0.5, r: 0.14, z: 0.245, c: C.dark, fan: true },
      ]),
    );
  return out;
};

/**
 * Dyno rooms: a block of test cells, louvred and windowless, a row of exhaust stacks on its
 * roof; more cells on the rest, each with its own stack and a generator now and then.
 */
const dynoRooms: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.08);
    const h = hallOn(i, 1 + steps(tier, 3, 4), '#B8BCC2', '#7C838B', 'louvre', 0);
    const n = Math.max(2, Math.floor(i.w / 0.35));
    for (let k = 0; k < n; k++)
      h.pieces.push({
        s: 'cyl',
        x: i.x + 0.18 + k * 0.35,
        y: i.y + 0.18,
        r: 0.05,
        z: h.h,
        h: 0.42 + (k % 2) * 0.08,
        c: C.flue,
        top: C.black,
      });
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces: h.pieces,
      fine: h.fine,
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r)) {
    if (hashXY(x, y, 5) < 0.3) {
      out.push(tile(p, x, y, generator(x, y)));
      continue;
    }
    out.push(
      tile(p, x, y, [
        plinth(x + 0.14, y + 0.18, 0.72, 0.64),
        bx(x + 0.14, y + 0.18, 0.72, 0.64, 0, 0.36, '#C2C6CB', {
          top: '#8E949C',
          roof: true,
          face: 'louvre',
          marks: { kind: 'grille', rows: 2, cols: 3, c: C.louvre, front: true },
        }),
        bx(x + 0.3, y + 0.81, 0.4, 0.02, 0, 0.26, C.door, { face: 'shutter' }),
        { s: 'cyl', x: x + 0.74, y: y + 0.3, r: 0.045, z: 0.36, h: 0.36, c: C.flue, top: C.black },
      ]),
    );
  }
  return out;
};

/**
 * Simulators: the simulator centre, dark glass and a dome on its roof; sim pods on the rest,
 * their wrap-round screens glowing.
 */
const simulators: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.08);
    const floors = 1 + steps(tier, 3, 4);
    const pieces = block(
      i.x,
      i.y,
      i.w,
      i.d,
      floors,
      '#3A4250',
      '#4A515C',
      '#4F6F8A',
      '#9FD8FF',
      0,
      'curtain',
    );
    const top = floors * FLOOR + 0.08;
    pieces.push(
      {
        s: 'cyl',
        x: i.x + i.w / 2,
        y: i.y + i.d / 2,
        r: Math.min(i.w, i.d) * 0.28,
        z: top,
        h: 0.08,
        c: '#D6D9DD',
        top: '#EEF0F2',
      },
      {
        s: 'ball',
        x: i.x + i.w / 2,
        y: i.y + i.d / 2,
        r: Math.min(i.w, i.d) * 0.22,
        z: top + 0.02,
        c: '#EEF0F2',
      },
      bx(i.x, i.y + i.d - 0.004, i.w, 0.012, 0.06, 0.04, '#5EE6FF', { glow: true }),
    );
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces,
      fine: parapet(i.x, i.y, i.w, i.d, top, '#3A4250'),
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r)) {
    const pieces: Piece[] = [bx(x + 0.06, y + 0.06, 0.88, 0.88, 0, 0.01, '#D6D2C8')];
    for (const dx of [0.28, 0.72]) {
      pieces.push(bx(x + dx - 0.14, y + 0.3, 0.28, 0.4, 0.01, 0.05, M.dark));
      pieces.push(bx(x + dx - 0.08, y + 0.42, 0.16, 0.18, 0.06, 0.06, M.flash));
      pieces.push(
        bx(x + dx - 0.15, y + 0.26, 0.3, 0.02, 0.06, 0.16, M.dark, {
          marks: { kind: 'band', rows: 1, cols: 1, c: '#2A3A4A', on: '#9FD8FF', z0: 0.15, z1: 0.9 },
        }),
      );
    }
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/**
 * Test tracks: the plot laid as a piece of test track, kerbs along its edges where it meets
 * other land, a car out on it; the timing hut on the first tile, a storey more with the tier.
 */
const testTracks: Model = (p) => {
  const tier = p.tier ?? 0;
  const own = new Set(p.tiles);
  const T = tilesOf(p);
  return T.map(([x, y], n) => {
    const pieces: Piece[] = [bx(x, y, 1, 1, 0, 0.014, M.tarmac)];
    if (!own.has(`${x},${y - 1}`)) pieces.push(...kerb(x, y + 0.02, 1, true, 0.014));
    if (!own.has(`${x},${y + 1}`)) pieces.push(...kerb(x, y + 0.91, 1, true, 0.014));
    if (!own.has(`${x - 1},${y}`)) pieces.push(...kerb(x + 0.02, y, 1, false, 0.014));
    if (!own.has(`${x + 1},${y}`)) pieces.push(...kerb(x + 0.91, y, 1, false, 0.014));
    pieces.push(bx(x + 0.1, y + 0.49, 0.8, 0.02, 0.014, 0.002, M.line));
    if (n === 0) {
      const floors = 1 + steps(tier, 4, 3);
      pieces.push(
        ...block(
          x + 0.2,
          y + 0.18,
          0.6,
          0.36,
          floors,
          M.white,
          C.roof,
          '#5C83A3',
          C.lit,
          0.014,
          'curtain',
        ),
      );
      pieces.push(
        bx(x + 0.18, y + 0.52, 0.64, 0.12, floors * FLOOR + 0.09, 0.025, M.team, { roof: true }),
      );
    } else if (hashXY(x, y, 23) < 0.5)
      pieces.push(
        ...car(
          x + 0.5,
          y + 0.7,
          true,
          hashXY(x, y, 29) < 0.5 ? 'gt' : 'proto',
          livery(x, y, 11),
          0.014,
        ),
      );
    return tile(p, x, y, pieces);
  });
};

/**
 * Hospitality units: the guests' two-storey glass units, a balcony and an awning on the front;
 * tables under parasols on the rest.
 */
const hospitality: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.1);
    const floors = 2 + steps(tier, 3, 3);
    const pieces = block(
      i.x,
      i.y,
      i.w,
      i.d - 0.15,
      floors,
      '#2E3238',
      '#3A3E45',
      '#6F93AF',
      C.lit,
      0,
      'curtain',
    );
    for (let f = 1; f < floors; f++)
      pieces.push(
        bx(i.x - 0.02, i.y + i.d - 0.2, i.w + 0.04, 0.2, f * FLOOR + 0.04, 0.025, M.white, {
          roof: true,
        }),
      );
    pieces.push(
      bx(i.x, i.y + i.d - 0.18, i.w, 0.03, floors * FLOOR + 0.08, 0.03, M.team, { roof: true }),
    );
    out.push({ x: r.x, y: r.y, w: r.w, d: r.h, pieces, id: p.id as string });
  }
  for (const [x, y] of rest(p, r)) {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.01, '#CFC8B8')];
    for (const [dx, dy] of [
      [0.3, 0.3],
      [0.7, 0.65],
    ] as const) {
      pieces.push({ s: 'cyl', x: x + dx, y: y + dy, r: 0.1, z: 0.12, h: 0.02, c: M.white });
      pieces.push({
        s: 'rod',
        a: [x + dx, y + dy, 0],
        b: [x + dx, y + dy, 0.4],
        t: 0.015,
        c: M.steel,
      });
      pieces.push({
        s: 'cyl',
        x: x + dx,
        y: y + dy,
        r: 0.2,
        z: 0.38,
        h: 0.03,
        c: hashXY(x, y + dy, 31) < 0.5 ? M.team : M.flash,
      });
    }
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/** Grand tourers: a glass showroom, its roof running out over the forecourt; GTs on show. */
const grandTourers: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.1);
    const floors = 1 + steps(tier, 4, 3);
    const pieces = block(
      i.x,
      i.y,
      i.w,
      i.d,
      floors,
      M.glassLight,
      '#E4E7EA',
      '#5C83A3',
      C.lit,
      0,
      'curtain',
    );
    const top = floors * FLOOR + 0.08;
    pieces.push(
      bx(i.x - 0.06, i.y - 0.02, i.w + 0.12, i.d + 0.18, top, 0.04, M.white, { roof: true }),
    );
    out.push({ x: r.x, y: r.y, w: r.w, d: r.h, pieces, id: p.id as string });
  }
  for (const [x, y] of rest(p, r)) {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.01, '#E4E1DA')];
    pieces.push({
      s: 'cyl',
      x: x + 0.5,
      y: y + 0.5,
      r: 0.38,
      z: 0.01,
      h: 0.025,
      c: '#C9CED3',
      top: '#DDE0E3',
    });
    pieces.push(...car(x + 0.5, y + 0.5, hashXY(x, y, 37) < 0.5, 'gt', livery(x, y, 13), 0.035));
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/**
 * Telemetry rooms: the data room, its screens glowing through the windows, masts and a dish on
 * its roof; aerial masts with their lights on the rest.
 */
const telemetry: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.1);
    const floors = 1 + steps(tier, 3, 4);
    const pieces = block(
      i.x,
      i.y,
      i.w,
      i.d,
      floors,
      '#D6D9DD',
      C.roof,
      '#3A5A7A',
      '#5EE6FF',
      0,
      'band',
    );
    const top = floors * FLOOR + 0.08;
    pieces.push(
      {
        s: 'rod',
        a: [i.x + 0.2, i.y + 0.2, top],
        b: [i.x + 0.2, i.y + 0.2, top + 0.7],
        t: 0.025,
        c: M.steel,
      },
      {
        s: 'cyl',
        x: i.x + 0.2,
        y: i.y + 0.2,
        r: 0.03,
        z: top + 0.7,
        h: 0.04,
        c: C.red,
        glow: true,
      },
      { s: 'cyl', x: i.x + i.w - 0.3, y: i.y + 0.35, r: 0.03, z: top, h: 0.18, c: M.steel },
      {
        s: 'panel',
        x: i.x + i.w - 0.5,
        y: i.y + 0.2,
        w: 0.4,
        d: 0.3,
        z: top + 0.18,
        rise: 0.28,
        c: M.white,
      },
    );
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces,
      fine: parapet(i.x, i.y, i.w, i.d, top, '#D6D9DD'),
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r)) {
    const tall = 0.8 + steps(tier, 4, 3) * 0.2;
    out.push(
      tile(p, x, y, [
        bx(x + 0.2, y + 0.2, 0.6, 0.6, 0, 0.03, C.pad),
        bx(x + 0.24, y + 0.5, 0.36, 0.26, 0.03, 0.22, M.white, { roof: true, face: 'clad' }),
        ...[
          [0.62, 0.28],
          [0.74, 0.4],
          [0.62, 0.4],
        ].map(([dx, dy]) => ({
          s: 'rod' as const,
          a: [x + (dx as number), y + (dy as number), 0] as [number, number, number],
          b: [x + 0.68, y + 0.34, tall] as [number, number, number],
          t: 0.02,
          c: '#C4382E',
        })),
        { s: 'cyl', x: x + 0.68, y: y + 0.34, r: 0.03, z: tall, h: 0.04, c: C.red, glow: true },
      ]),
    );
  }
  return out;
};

/**
 * Carbon works: a clean white hall with extraction on its roof, autoclaves standing beside it,
 * more of them as it grows.
 */
const carbonWorks: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.08);
    const h = hallOn(i, 1 + steps(tier, 3, 4), '#F1F2F3', '#D6D9DD', 'clad', 2);
    h.pieces.push(
      {
        s: 'cyl',
        x: i.x + i.w - 0.25,
        y: i.y + 0.25,
        r: 0.08,
        z: h.h,
        h: 0.3,
        c: '#C9CED3',
        top: '#9AA1AA',
      },
      bx(i.x + 0.1, i.y + i.d * 0.4, i.w * 0.5, 0.012, h.h + 0.02, 0.012, M.dark),
    );
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces: h.pieces,
      fine: h.fine,
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r))
    out.push(
      tile(p, x, y, [
        bx(x + 0.08, y + 0.08, 0.84, 0.84, 0, 0.03, C.pad),
        {
          s: 'cyl',
          x: x + 0.5,
          y: y + 0.5,
          r: 0.3,
          z: 0.03,
          h: 0.32 + steps(tier, 4, 3) * 0.06,
          c: '#B9C0C8',
          top: '#D6D9DD',
        },
        { s: 'cyl', x: x + 0.5, y: y + 0.82, r: 0.12, z: 0.1, h: 0.02, c: M.steel },
        bx(x + 0.1, y + 0.1, 0.16, 0.16, 0.03, 0.2, M.dark, { roof: true }),
      ]),
    );
  return out;
};

/**
 * Endurance cars: a garage for the long races, its apron floodlit at night from a mast, the
 * prototype out front; the garage gains a storey with the tier.
 */
const enduranceCars: Model = (p) => {
  const tier = p.tier ?? 0;
  const floors = steps(tier, 3, 3);
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [bx(x + 0.02, y + 0.02, 0.96, 0.96, 0, 0.012, M.paddock)];
    pieces.push(plinth(x + 0.04, y + 0.06, 0.92, 0.42));
    pieces.push(
      bx(x + 0.04, y + 0.06, 0.92, 0.42, 0, 0.42, '#2E3238', {
        top: '#3A3E45',
        roof: floors === 0,
        face: 'clad',
      }),
    );
    pieces.push(...openBay(x + 0.14, y + 0.48, 0.72, 0.3, M.flash));
    if (floors > 0)
      pieces.push(
        ...block(
          x + 0.04,
          y + 0.06,
          0.92,
          0.42,
          floors,
          '#2E3238',
          '#3A3E45',
          '#4F6F8A',
          C.lit,
          0.42,
          'band',
        ),
      );
    pieces.push(
      ...car(
        x + 0.5,
        y + 0.74,
        true,
        'proto',
        hashXY(x, y, 41) < 0.5 ? M.team : livery(x, y, 15),
        0.012,
      ),
    );
    if (hashXY(x, y, 43) < 0.5) {
      pieces.push({
        s: 'rod',
        a: [x + 0.08, y + 0.92, 0],
        b: [x + 0.08, y + 0.92, 0.9],
        t: 0.025,
        c: M.steel,
      });
      pieces.push(bx(x + 0.03, y + 0.88, 0.12, 0.06, 0.9, 0.06, M.warm, { glow: true }));
    }
    return tile(p, x, y, pieces);
  });
};

/**
 * Electric racers: charging bays under a solar canopy, an open-wheel electric racer at each,
 * its charger's band glowing.
 */
const electricRacers: Model = (p) => {
  const tier = p.tier ?? 0;
  return tilesOf(p).map(([x, y]) => {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.012, '#D6D9DD')];
    pieces.push(...car(x + 0.5, y + 0.58, true, 'open', livery(x, y, 17), 0.012));
    pieces.push(
      bx(x + 0.12, y + 0.18, 0.12, 0.1, 0.012, 0.3, M.white, {
        roof: true,
        marks: { kind: 'band', rows: 1, cols: 1, c: '#2A3A4A', on: '#5BE37D', z0: 0.6, z1: 0.8 },
      }),
    );
    const h = 0.5 + steps(tier, 5, 2) * 0.06;
    for (const [px, py] of [
      [0.12, 0.14],
      [0.88, 0.14],
    ] as const)
      pieces.push({
        s: 'rod',
        a: [x + px, y + py, 0],
        b: [x + px, y + py, h],
        t: 0.025,
        c: M.steel,
      });
    pieces.push({
      s: 'panel',
      x: x + 0.08,
      y: y + 0.1,
      w: 0.84,
      d: 0.8,
      z: h,
      rise: 0.12,
      c: C.solar,
    });
    return tile(p, x, y, pieces);
  });
};

/**
 * Hypercars: the atelier, dark glass with a light line round it; hypercars on lit turntables on
 * the rest.
 */
const hypercars: Model = (p) => {
  const tier = p.tier ?? 0;
  const r = mainRect(p.tiles);
  const out: Unit[] = [];
  if (r.w > 0) {
    const i = inset(r.x, r.y, r.w, r.h, 0.1);
    const floors = 1 + steps(tier, 3, 4);
    const pieces = block(
      i.x,
      i.y,
      i.w,
      i.d,
      floors,
      '#1F2329',
      '#2B2F36',
      '#3A4A5C',
      C.lit,
      0,
      'curtain',
    );
    const top = floors * FLOOR + 0.08;
    pieces.push(
      bx(i.x - 0.01, i.y - 0.01, i.w + 0.02, i.d + 0.02, top - 0.04, 0.02, M.gold, { glow: true }),
      bx(i.x - 0.01, i.y - 0.01, i.w + 0.02, i.d + 0.02, 0.05, 0.015, M.gold, { glow: true }),
    );
    out.push({
      x: r.x,
      y: r.y,
      w: r.w,
      d: r.h,
      pieces,
      fine: parapet(i.x, i.y, i.w, i.d, top, '#1F2329'),
      id: p.id as string,
    });
  }
  for (const [x, y] of rest(p, r)) {
    const pieces: Piece[] = [bx(x + 0.04, y + 0.04, 0.92, 0.92, 0, 0.01, '#2E3238')];
    pieces.push({ s: 'disc', x: x + 0.5, y: y + 0.5, r: 0.42, z: 0.01, c: M.gold, glow: true });
    pieces.push({
      s: 'cyl',
      x: x + 0.5,
      y: y + 0.5,
      r: 0.38,
      z: 0.01,
      h: 0.025,
      c: '#3A3E45',
      top: '#4A4F58',
    });
    pieces.push(...car(x + 0.5, y + 0.5, hashXY(x, y, 47) < 0.5, 'hyper', livery(x, y, 19), 0.035));
    out.push(tile(p, x, y, pieces));
  }
  return out;
};

/** Each producer's own look, by id. A producer with none takes the plain workshop. */
export const MODELS: Record<string, Model> = {
  go_karts: goKarts,
  club_cars: clubCars,
  tyre_bays: tyreBays,
  pit_crews: pitCrews,
  rally_cars: rallyCars,
  engine_shops: engineShops,
  workshops,
  touring_cars: touringCars,
  wind_tunnels: windTunnels,
  dyno_rooms: dynoRooms,
  simulators,
  test_tracks: testTracks,
  hospitality_units: hospitality,
  grand_tourers: grandTourers,
  telemetry_rooms: telemetry,
  carbon_works: carbonWorks,
  endurance_cars: enduranceCars,
  electric_racers: electricRacers,
  hypercars,
};

/** A producer's plot as units. Empty for land with no producer on it. */
export function plotUnits(p: IsoParcel, L: Look): Unit[] {
  if (!p.id || p.t !== 'field' || !p.tiles.length) return [];
  return (MODELS[p.id] ?? workshops)(p, L);
}

/** D353: the tallest the dyno cells' stacks stand on a plot, for their exhaust. */
export function stackTops(units: readonly Unit[]): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  for (const u of units)
    for (const q of u.pieces) if (q.s === 'cyl' && q.c === C.flue) out.push([q.x, q.y, q.z + q.h]);
  return out;
}

/* ---------- the yard, the chains and the marks ---------- */

/** D291: the paint decoration's walls and the roof, from `look` (`paint+roof`). */
function decorOf(look?: string): { wall?: string; roof?: string; stripes?: boolean } {
  const [b, r] = (look ?? '').split('+');
  return {
    ...(PAINT[b ?? ''] ? { wall: PAINT[b ?? ''] } : {}),
    ...(ROOFS[r ?? ''] ? { roof: ROOFS[r ?? ''] } : {}),
    ...(b === 'paint_stripes' || b === 'paint_checks' ? { stripes: true } : {}),
  };
}

/** How many storeys a building's producer's milestones give it (D268's `style`). */
function styleFloors(style?: string): number {
  return style === 'stone' ? 3 : style === 'timber' ? 2 : 1;
}

/**
 * The yard's two buildings and each chain's building. The house is the team's office, brick and
 * glass with its flag; the barn is the parts store, racked and shuttered, wider with its level.
 * A chain's building has its own silhouette by what it is, taller as its producer grows.
 */
export function buildingUnits(b: IsoBuildingAt): Unit[] {
  const dec = decorOf(b.look);
  const { x, y, w, h } = b;
  const i = inset(x, y, w, h, 0.08);
  const pieces: Piece[] = [];
  const unit = (ps: Piece[], fine: Piece[] = []): Unit[] => [
    { x, y, w, d: h, pieces: ps, fine, ...(b.id ? { id: b.id } : {}) },
  ];
  const stripe = (q: { x: number; y: number; w: number; d: number }, z: number) =>
    dec.stripes ? [bx(q.x - 0.006, q.y - 0.006, q.w + 0.012, q.d + 0.012, z, 0.04, M.flash)] : [];
  if (b.kind === 'house') {
    const ow = i.w * 0.62;
    pieces.push(
      ...block(
        i.x,
        i.y,
        ow,
        i.d,
        2,
        dec.wall ?? '#B4694A',
        dec.roof ?? '#5A5F66',
        C.window,
        C.lit,
        0,
        'punched',
      ),
    );
    pieces.push(...stripe({ x: i.x, y: i.y, w: ow, d: i.d }, 0.36));
    // The glass entrance, the team's band over it, the flag on its pole.
    pieces.push(
      bx(i.x + ow, i.y + 0.25, i.w - ow, i.d - 0.25, 0, FLOOR + 0.1, M.glassLight, {
        top: dec.roof ?? C.light,
        roof: true,
        face: 'curtain',
        marks: { kind: 'win', rows: 1, cols: 3, c: C.window, on: C.lit, z0: 0.08, z1: 0.9 },
      }),
      bx(i.x + ow - 0.02, i.y + i.d - 0.02, i.w - ow + 0.02, 0.03, FLOOR + 0.1, 0.06, M.team),
      { s: 'cyl', x: i.x + i.w - 0.15, y: i.y + 0.15, r: 0.02, z: 0, h: 1.1, c: '#E4E7EA' },
      bx(i.x + i.w - 0.13, i.y + 0.14, 0.32, 0.02, 0.84, 0.2, M.team),
      bx(i.x + i.w - 0.13, i.y + 0.14, 0.32, 0.021, 0.84, 0.05, M.flash),
    );
    return unit(pieces, parapet(i.x, i.y, ow, i.d, 2 * FLOOR + 0.08, dec.wall ?? '#B4694A'));
  }
  if (b.kind === 'barn') {
    // The parts store grows with its level (its width, 2 to 4) from the team's lock-up to its
    // works: a row of lock-ups, the team's door in its colours (2 wide); the race workshop
    // with its offices (3); the works factory with its glass front (4).
    const stage = w <= 2.5 ? 1 : w <= 3.5 ? 3 : 4;
    const wall = dec.wall ?? (stage >= 4 ? M.white : stage === 1 ? '#D8D2C4' : '#C9CED3');
    const roofC = dec.roof ?? (stage === 1 ? C.roof : '#8C939B');
    const hh = stage === 1 ? 0.42 : stage === 3 ? 0.62 : 0.82;
    pieces.push(
      plinth(i.x, i.y, i.w, i.d),
      bx(i.x, i.y, i.w, i.d, 0, hh, wall, {
        top: roofC,
        roof: true,
        face: stage === 1 ? 'clad' : 'ribbed',
      }),
      ...stripe(i, 0.3),
    );
    if (stage > 1)
      pieces.push(bx(i.x - 0.006, i.y - 0.006, i.w + 0.012, i.d + 0.012, hh - 0.09, 0.05, M.team));
    let doorsFrom = i.x,
      doorsW = i.w;
    if (stage === 3) {
      // The office: two glazed storeys at the end of the workshop.
      const ow = Math.min(0.9, i.w * 0.35);
      pieces.push(
        ...block(
          i.x,
          i.y + 0.1,
          ow,
          i.d - 0.1,
          2,
          M.glassLight,
          C.roof,
          '#5C83A3',
          C.lit,
          0,
          'curtain',
        ),
      );
      doorsFrom = i.x + ow;
      doorsW = i.w - ow;
    }
    if (stage === 4) {
      // The glass front, full height, the team's name board over it, flags at the door.
      const gw = i.w * 0.4;
      pieces.push(
        bx(i.x, i.y + i.d - 0.32, gw, 0.34, 0, hh + 0.12, M.glassLight, {
          top: M.white,
          roof: true,
          face: 'curtain',
          marks: { kind: 'win', rows: 2, cols: 5, c: C.window, on: C.lit, z0: 0.06, z1: 0.96 },
        }),
        bx(i.x + 0.05, i.y + i.d + 0.015, gw - 0.1, 0.02, hh - 0.12, 0.1, M.team, { face: 'sign' }),
      );
      for (const k of [0, 1, 2])
        pieces.push(
          {
            s: 'cyl',
            x: i.x + 0.12 + k * 0.14,
            y: i.y + i.d + 0.25,
            r: 0.015,
            z: 0,
            h: 0.75,
            c: '#E4E7EA',
          },
          bx(
            i.x + 0.13 + k * 0.14,
            i.y + i.d + 0.245,
            0.14,
            0.01,
            0.58,
            0.14,
            k === 1 ? M.flash : M.team,
          ),
        );
      doorsFrom = i.x + gw;
      doorsW = i.w - gw;
    }
    const bays =
      stage === 1 ? Math.max(1, Math.round(doorsW / 0.9)) : Math.max(1, Math.round(doorsW * 1.2));
    const dw = stage === 1 ? 0.6 : 0.32;
    for (let k = 0; k < bays; k++) {
      const sx = doorsFrom + ((k + 0.5) * doorsW) / bays - dw / 2;
      // A row of lock-ups: a wall between each, standing proud of the roof.
      if (stage === 1 && k > 0)
        pieces.push(
          bx(doorsFrom + (k * doorsW) / bays - 0.02, i.y, 0.04, i.d + 0.02, 0, hh + 0.03, wall),
        );
      pieces.push(
        bx(sx - 0.02, i.y + i.d - 0.01, dw + 0.04, 0.08, 0, 0.012, C.pad),
        bx(
          sx,
          i.y + i.d - 0.005,
          dw,
          0.025,
          0,
          Math.min(0.34, hh - 0.08),
          stage === 1 && k === 0 ? M.team : '#A9B0B8',
          {
            face: 'shutter',
            marks: { kind: 'grille', rows: 5, cols: 1, c: '#9DA1A6', front: true },
          },
        ),
      );
    }
    if (stage > 1)
      pieces.push(
        ...roofPlant(i.x + 0.1, i.y + 0.1, i.w - 0.2, i.d - 0.4, hh, Math.min(6, bays + stage)),
      );
    return unit(pieces, stage > 1 ? parapet(i.x, i.y, i.w, i.d, hh, wall) : []);
  }
  const floors = styleFloors(b.style);
  const c = b.chain ?? '';
  const W = dec.wall ?? M.white,
    R = dec.roof ?? C.roof;
  if (/desk|office|room/.test(c)) {
    pieces.push(
      ...block(
        i.x,
        i.y,
        i.w,
        i.d,
        floors + 1,
        dec.wall ?? M.glassLight,
        dec.roof ?? '#C7CCD2',
        '#5C83A3',
        C.lit,
        0,
        'curtain',
      ),
    );
    pieces.push(
      bx(i.x - 0.01, i.y + i.d - 0.01, i.w + 0.02, 0.03, (floors + 1) * FLOOR + 0.02, 0.05, M.team),
    );
  } else if (/school/.test(c)) {
    pieces.push(
      ...block(i.x, i.y, i.w, i.d, floors, dec.wall ?? '#E7DCC8', R, C.window, C.lit, 0, 'punched'),
    );
    const top = floors * FLOOR + 0.08;
    pieces.push({ s: 'cyl', x: i.x + 0.12, y: i.y + 0.12, r: 0.02, z: top, h: 0.6, c: '#E4E7EA' });
    for (let k = 0; k < 4; k++)
      pieces.push(
        bx(
          i.x + 0.14 + (k % 2) * 0.1,
          i.y + 0.11,
          0.1,
          0.02,
          top + 0.44 + Math.floor(k / 2) * 0.08,
          0.08,
          k % 3 ? M.dark : M.white,
        ),
      );
  } else if (/studio/.test(c)) {
    pieces.push(
      ...block(
        i.x,
        i.y,
        i.w,
        i.d,
        floors,
        dec.wall ?? '#2E3238',
        R,
        '#3A5A7A',
        '#9FD8FF',
        0,
        'band',
      ),
    );
    pieces.push(
      bx(i.x, i.y + i.d - 0.004, i.w, 0.012, floors * FLOOR - 0.02, 0.04, '#5EE6FF', {
        glow: true,
      }),
    );
  } else if (c === 'tyre_store') {
    pieces.push(...block(i.x, i.y, i.w, i.d, floors, W, R, C.window, C.lit, 0, 'ribbed'));
    const top = floors * FLOOR + 0.08;
    pieces.push(...tyres(i.x + i.w * 0.5, i.y + i.d * 0.5, 2 + floors, M.soft, top, 0.16));
  } else if (c === 'rally_service') {
    pieces.push(bx(i.x, i.y, i.w, i.d, 0, 0.012, M.gravel));
    pieces.push(
      ...canopy(i.x + 0.05, i.y + 0.05, i.w - 0.1, i.d * 0.6, 0.4 + floors * 0.05, M.team),
    );
    pieces.push(...car(i.x + i.w / 2, i.y + i.d * 0.35, true, 'rally', M.flash, 0.012));
  } else if (/garage|racing/.test(c)) {
    pieces.push(plinth(i.x, i.y, i.w, i.d * 0.6));
    pieces.push(
      bx(i.x, i.y, i.w, i.d * 0.6, 0, 0.42 + (floors - 1) * FLOOR, W, {
        top: R,
        roof: true,
        face: 'clad',
      }),
    );
    pieces.push(...openBay(i.x + i.w * 0.15, i.y + i.d * 0.6, i.w * 0.7, 0.3, M.team));
    pieces.push(
      ...car(
        i.x + i.w / 2,
        i.y + i.d * 0.82,
        true,
        c === 'endurance_garage' ? 'proto' : 'gt',
        M.flash,
        0,
      ),
    );
  } else if (/lounge/.test(c)) {
    pieces.push(
      ...block(
        i.x,
        i.y,
        i.w,
        i.d - 0.12,
        floors + 1,
        '#2E3238',
        '#3A3E45',
        '#6F93AF',
        C.lit,
        0,
        'curtain',
      ),
    );
    pieces.push(
      bx(i.x - 0.02, i.y + i.d - 0.18, i.w + 0.04, 0.18, FLOOR + 0.04, 0.025, M.white, {
        roof: true,
      }),
    );
  } else if (/lab/.test(c)) {
    pieces.push(...block(i.x, i.y, i.w, i.d, floors, W, R, '#5C83A3'));
    pieces.push(bx(i.x, i.y + i.d - 0.004, i.w, 0.012, 0.1, 0.04, '#5BE37D', { glow: true }));
  } else if (/shop|counter|bay|sales/.test(c)) {
    pieces.push(...block(i.x, i.y, i.w, i.d, floors, W, R, C.window, C.lit, 0, 'clad'));
    pieces.push(
      bx(i.x + i.w * 0.2, i.y + i.d - 0.005, i.w * 0.6, 0.025, 0, 0.26, '#B9BCC0', {
        face: 'shutter',
      }),
      bx(i.x + 0.05, i.y + i.d - 0.002, i.w - 0.1, 0.022, floors * FLOOR - 0.06, 0.08, M.team, {
        face: 'sign',
      }),
    );
  } else pieces.push(...block(i.x, i.y, i.w, i.d, floors, W, R));
  return unit(pieces);
}

/** The flat map's festival mark footprint half-size (as `MARK_HALF`). */
const MH = 0.7;

/** D245: a race day's lasting mark, each its own small landmark. */
export function markUnits(kind: string, x: number, y: number): Unit[] {
  const pieces: Piece[] = [];
  switch (kind) {
    case 'founders_plaque':
      pieces.push(bx(x - 0.25, y - 0.15, 0.5, 0.3, 0, 0.2, '#9EA19C', { roof: true }), {
        s: 'panel',
        x: x - 0.2,
        y: y - 0.1,
        w: 0.4,
        d: 0.2,
        z: 0.2,
        rise: 0.12,
        c: C.copper,
      });
      break;
    case 'podium':
      pieces.push(
        bx(x - 0.12, y - 0.15, 0.24, 0.3, 0, 0.3, M.white, { roof: true }),
        bx(x - 0.36, y - 0.15, 0.24, 0.3, 0, 0.2, M.white, { roof: true }),
        bx(x + 0.12, y - 0.15, 0.24, 0.3, 0, 0.13, M.white, { roof: true }),
        bx(x - 0.12, y + 0.15, 0.24, 0.006, 0.12, 0.08, M.gold),
        bx(x - 0.36, y + 0.15, 0.24, 0.006, 0.07, 0.06, '#B9BFC6'),
        bx(x + 0.12, y + 0.15, 0.24, 0.006, 0.04, 0.05, C.copper),
        bx(x - 0.4, y - 0.2, 0.8, 0.04, 0, 0.6, M.team),
      );
      break;
    case 'flag_tower':
      for (const [dx, dy] of [
        [-0.15, -0.15],
        [0.15, -0.15],
        [0.15, 0.15],
        [-0.15, 0.15],
      ] as const)
        pieces.push({
          s: 'rod',
          a: [x + dx, y + dy, 0],
          b: [x + dx * 0.6, y + dy * 0.6, 1.2],
          t: 0.03,
          c: M.steel,
        });
      pieces.push(bx(x - 0.15, y - 0.15, 0.3, 0.3, 1.2, 0.18, M.white, { roof: true }));
      for (let k = 0; k < 8; k++)
        pieces.push(
          bx(
            x + 0.16 + (k % 4) * 0.08,
            y - 0.01,
            0.08,
            0.02,
            1.36 - Math.floor(k / 4) * 0.08,
            0.08,
            (k + Math.floor(k / 4)) % 2 ? M.white : M.dark,
          ),
        );
      break;
    case 'grandstand':
      for (let k = 0; k < 4; k++)
        pieces.push(
          bx(x - 0.5, y - 0.3 + k * 0.12, 1, 0.12, 0, 0.36 - k * 0.08, k % 2 ? M.team : '#C9CED3', {
            roof: true,
          }),
        );
      for (const dx of [-0.48, 0.46])
        pieces.push({
          s: 'rod',
          a: [x + dx, y - 0.3, 0.36],
          b: [x + dx, y - 0.3, 0.6],
          t: 0.03,
          c: M.steel,
        });
      pieces.push({
        s: 'panel',
        x: x - 0.55,
        y: y - 0.34,
        w: 1.1,
        d: 0.5,
        z: 0.58,
        rise: -0.06,
        c: M.white,
      });
      break;
    case 'timing_tower':
      pieces.push(
        bx(x - 0.12, y - 0.12, 0.24, 0.24, 0, 1.5, '#2E3238', { roof: true }),
        bx(x - 0.1, y + 0.12, 0.2, 0.012, 0.3, 1.1, '#2E3238', {
          marks: {
            kind: 'led',
            rows: 10,
            cols: 2,
            c: '#3A3E45',
            on: M.medium,
            lit: 1,
            front: true,
          },
        }),
        bx(x - 0.14, y - 0.14, 0.28, 0.28, 1.5, 0.08, M.team, { roof: true }),
      );
      break;
    case 'tyre_sculpture': {
      const cols = [M.soft, M.medium, M.hard, M.wet, '#3FA35A'];
      let n = 0;
      for (let row = 0; row < 4; row++)
        for (let k = 0; k <= 3 - row; k++) {
          const px = x - 0.36 + k * 0.24 + row * 0.12;
          pieces.push({
            s: 'cyl',
            x: px,
            y,
            r: 0.11,
            z: row * 0.09,
            h: 0.085,
            c: M.tyre,
            top: cols[n++ % cols.length],
          });
        }
      break;
    }
    case 'trophy_cabinet':
      pieces.push(
        bx(x - 0.3, y - 0.2, 0.6, 0.4, 0, 0.12, '#6B5A44', { roof: true }),
        bx(x - 0.28, y - 0.18, 0.56, 0.36, 0.12, 0.42, M.glassLight, {
          top: '#C9D9E4',
          roof: true,
        }),
        { s: 'cyl', x, y, r: 0.05, z: 0.14, h: 0.12, c: M.gold },
        { s: 'ball', x, y, r: 0.08, z: 0.24, c: M.gold },
      );
      break;
    case 'flagpole':
      pieces.push({ s: 'cyl', x, y, r: 0.03, z: 0, h: 1.5, c: '#E4E7EA' });
      for (let k = 0; k < 6; k++)
        pieces.push(
          bx(
            x + 0.03 + (k % 3) * 0.15,
            y - 0.01,
            0.15,
            0.02,
            1.32 - Math.floor(k / 3) * 0.14,
            0.14,
            (k + Math.floor(k / 3)) % 2 ? M.white : M.dark,
          ),
        );
      break;
    case 'footbridge':
      for (const dx of [-0.55, 0.45])
        pieces.push(bx(x + dx, y - 0.15, 0.1, 0.3, 0, 0.5, C.light, { roof: true }));
      pieces.push(
        bx(x - 0.6, y - 0.15, 1.2, 0.3, 0.5, 0.06, C.mid, { top: '#B9BFC6', roof: true }),
        bx(x - 0.6, y + 0.15, 1.2, 0.01, 0.56, 0.12, M.team),
        {
          s: 'rod',
          a: [x - 0.6, y - 0.15, 0.68],
          b: [x + 0.6, y - 0.15, 0.68],
          t: 0.02,
          c: C.steel,
        },
      );
      break;
    case 'floodlights':
      pieces.push(
        bx(x - 0.1, y - 0.1, 0.2, 0.2, 0, 0.08, '#9EA19C', { roof: true }),
        { s: 'cyl', x, y, r: 0.04, z: 0.08, h: 1.6, c: M.steel },
        bx(x - 0.25, y - 0.05, 0.5, 0.1, 1.66, 0.14, '#3B4048'),
        bx(x - 0.24, y + 0.05, 0.48, 0.01, 1.68, 0.1, M.warm, { glow: true }),
      );
      break;
    default:
      pieces.push(bx(x - 0.25, y - 0.25, 0.5, 0.5, 0, 0.4, '#9EA19C', { roof: true }));
  }
  return [{ x: x - MH, y: y - MH, w: MH * 2, d: MH * 2, pieces }];
}

/* ---------- D420: the circuit beyond the fence, for both renderers ---------- */

/**
 * The circuit's centre line, a closed loop of points from the `mcircuit` country item's box:
 * a long straight along the land, a hairpin, a chicane and a sweep back, smoothed. Points are in
 * world tiles, the same on every device.
 */
export function circuitPath(b: Bounds, r = 0, flip = 0): Array<[number, number]> {
  const { x, y } = b;
  // Laid along y, or along x when `r` is 1: `w` across the loop, `h` along it.
  const w = r ? b.h : b.w,
    h = r ? b.w : b.h;
  const key: Array<[number, number]> = [
    [w - 1, h - 2.5],
    [w - 1, 2.5],
    [w - 1.6, 0.8],
    [w * 0.55, 0.8],
    [w * 0.4, 2.4],
    [w * 0.6, 4.2],
    [w * 0.5, 6],
    [w * 0.18, 7.5],
    [1, h * 0.55],
    [1, h - 2.5],
    [2, h - 0.9],
    [w - 2, h - 0.9],
  ];
  // Mirrored across the loop (`flip`), so its main straight lies on the other side.
  let pts = key.map(([u0, v]) => {
    const u = flip ? w - u0 : u0;
    return (r ? [x + v, y + u] : [x + u, y + v]) as [number, number];
  });
  // Chaikin's corner cutting, three times round the loop.
  for (let it = 0; it < 3; it++) {
    const next: Array<[number, number]> = [];
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i] as [number, number],
        c = pts[(i + 1) % pts.length] as [number, number];
      next.push([a[0] * 0.75 + c[0] * 0.25, a[1] * 0.75 + c[1] * 0.25]);
      next.push([a[0] * 0.25 + c[0] * 0.75, a[1] * 0.25 + c[1] * 0.75]);
    }
    pts = next;
  }
  return pts;
}

/** The circuit's width, its kerbs' and its run-off's, in tiles. */
export const CIRCUIT = { width: 0.9, kerb: 0.12, runoff: 0.9 } as const;

/** How sharply the loop turns at point `i`, 0 straight to about 1 a hairpin. */
export function bendAt(pts: ReadonlyArray<[number, number]>, i: number): number {
  const n = pts.length;
  const a = pts[(i - 2 + n) % n] as [number, number],
    b = pts[i] as [number, number],
    c = pts[(i + 2) % n] as [number, number];
  const u = Math.atan2(b[1] - a[1], b[0] - a[0]),
    v = Math.atan2(c[1] - b[1], c[0] - b[0]);
  let d = Math.abs(v - u);
  if (d > Math.PI) d = 2 * Math.PI - d;
  return d;
}

/** Which side the bend turns towards at point `i`: +1 left, -1 right. */
export function bendSide(pts: ReadonlyArray<[number, number]>, i: number): number {
  const n = pts.length;
  const a = pts[(i - 2 + n) % n] as [number, number],
    b = pts[i] as [number, number],
    c = pts[(i + 2) % n] as [number, number];
  return Math.sign((b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0])) || 1;
}

/** Each point's unit normal, to the left of the way the loop runs. */
export function loopNormals(pts: ReadonlyArray<[number, number]>): Array<[number, number]> {
  const n = pts.length;
  return pts.map((_, i) => {
    const a = pts[(i - 1 + n) % n] as [number, number],
      c = pts[(i + 1) % n] as [number, number];
    const dx = c[0] - a[0],
      dy = c[1] - a[1],
      l = Math.hypot(dx, dy) || 1;
    return [-dy / l, dx / l];
  });
}

/** The loop moved `d` to its left (a negative `d` to its right). */
export function offsetLoop(
  pts: ReadonlyArray<[number, number]>,
  d: number,
): Array<[number, number]> {
  const N = loopNormals(pts);
  return pts.map((p, i) => [
    p[0] + (N[i] as [number, number])[0] * d,
    p[1] + (N[i] as [number, number])[1] * d,
  ]);
}
